import type { BadgeTone } from '@platform/ui';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { Messages } from '../i18n';
import {
    endpoints,
    type ChannelAccount,
    type ChannelDescriptor,
    type ChannelSyncStatus,
    type CreateChannelAccountBody,
    type UpdateChannelAccountBody,
} from './api';
import { useOwnStoreName } from './app-config';
import { useCanOpen, usePermittedMutation } from './session';
import { useStorefrontSites } from './storefront';

/**
 * Sales channel hooks.
 *
 * The channel catalogue is a property of the deployment rather than of the
 * seller, so it is cached for the session: it cannot change while the panel is
 * open, and refetching it on every visit to the screen buys nothing.
 */

export function useChannels() {
    return useQuery({
        queryKey: ['channels'],
        queryFn: endpoints.listChannels,
        staleTime: Infinity,
    });
}

export function useChannelAccounts() {
    return useQuery({
        queryKey: ['channel-accounts'],
        queryFn: endpoints.listChannelAccounts,
    });
}

/** The name a connection's channel goes by: the own store's brand, or the name the marketplace's adapter declares. */
export function channelLabel(account: ChannelAccount, channel: ChannelDescriptor | undefined, ownStoreName: string): string {
    return channel?.kind === 'own_store' ? ownStoreName : (channel?.displayName ?? account.channelKey);
}

/**
 * A connection as it is told apart from the others (docs/15 §2).
 *
 * The seller names each connection, and nothing stops them giving the same
 * shop name on two marketplaces: "Relegans" twice is two connections nobody can
 * tell apart, and a price typed under the wrong one is sent to the wrong
 * marketplace. So the channel comes first and the seller's name after it, once
 * when they read the same. Until the channel catalogue has loaded the name
 * stands alone rather than beside a raw channel key.
 */
export function connectionName(account: ChannelAccount, channel: ChannelDescriptor | undefined, ownStoreName: string): string {
    if (!channel) return account.name;
    const label = channelLabel(account, channel, ownStoreName);
    const name = account.name.trim();
    if (name === '' || name.localeCompare(label, 'tr', { sensitivity: 'base' }) === 0) return label;
    return `${label} · ${name}`;
}

/**
 * A connection as a record names it (docs/15 §2, ADR 0009 §2): `name` is the
 * connection in full (connectionName), which is what a record and a filter
 * both show, `logo` the channel's mark where it has one and `logoDark` its
 * twin for a dark ground (ADR 0009 §1).
 */
export type ConnectionMark = { name: string; logo?: string; logoDark?: string };

/**
 * Every connection's mark by its id, for screens that hold only the id.
 *
 * A record names the connection in full, so the shop an order came from is on
 * the row whether or not the seller yet holds a second one on that marketplace
 * (ADR 0009 §2, EPZ-0056). connectionName drops the repetition where the
 * connection is named after its own channel.
 */
export function useConnectionMarks(): Map<string, ConnectionMark> {
    const channels = useChannels();
    const accounts = useChannelAccounts();
    const ownStoreName = useOwnStoreName();
    return useMemo(() => {
        const byKey = new Map((channels.data?.channels ?? []).map((channel) => [channel.key, channel] as const));
        return new Map(
            (accounts.data?.accounts ?? []).map((account) => {
                const channel = byKey.get(account.channelKey);
                return [
                    account.id,
                    {
                        name: connectionName(account, channel, ownStoreName),
                        logo: channel?.logoUrl,
                        logoDark: channel?.logoDarkUrl,
                    },
                ] as const;
            }),
        );
    }, [channels.data, accounts.data, ownStoreName]);
}

/** Every connection's name (connectionName) by its id, for filters and pickers that hold only the id. */
export function useConnectionNames(): Map<string, string> {
    const marks = useConnectionMarks();
    return useMemo(() => new Map([...marks].map(([id, mark]) => [id, mark.name] as const)), [marks]);
}

/**
 * What the channel behind a connected account declares it can do.
 *
 * Every screen that decides whether an action is worth offering needs the same
 * two lookups — account by id, then channel by key — and doing them inline in
 * each component is how one of them ends up branching on a channel name
 * instead. `can` reads a capability without the caller holding the array.
 */
export function useAccountChannel(accountId: string | undefined) {
    const channels = useChannels();
    const accounts = useChannelAccounts();
    const account = accounts.data?.accounts.find((candidate) => candidate.id === accountId);
    const channel = channels.data?.channels.find((candidate) => candidate.key === account?.channelKey);
    return {
        channel,
        capabilities: channel?.capabilities ?? [],
        can: (capability: string) => (channel?.capabilities ?? []).includes(capability),
    };
}

/** One marketplace the company has connected, as the Eşleştirme tabs choose between them. */
export interface ConnectedMarketplace {
    key: string;
    label: string;
    channel: ChannelDescriptor;
    /** The connections to it that work, oldest first: a brand request goes through one. */
    accountIds: string[];
}

/**
 * One entry per marketplace channel the company has connected, in the order
 * the connections were made. Mapping keys on the channel, not the connection,
 * so two stores on one marketplace are one entry; a connection switched off
 * still counts, since what is mapped for it stays mapped.
 */
export function useConnectedMarketplaces() {
    const channels = useChannels();
    const accounts = useChannelAccounts();
    const marketplaces = useMemo(() => {
        const byKey = new Map(channels.data?.channels.map((channel) => [channel.key, channel]) ?? []);
        const out: ConnectedMarketplace[] = [];
        for (const account of accounts.data?.accounts ?? []) {
            const channel = byKey.get(account.channelKey);
            if (!channel || channel.kind !== 'marketplace') continue;
            let entry = out.find((candidate) => candidate.key === account.channelKey);
            if (!entry) {
                entry = { key: account.channelKey, label: channel.displayName, channel, accountIds: [] };
                out.push(entry);
            }
            if (account.status !== 'disabled') entry.accountIds.push(account.id);
        }
        return out;
    }, [channels.data, accounts.data]);
    return { marketplaces, isPending: channels.isPending || accounts.isPending };
}

/**
 * Whether a channel takes a brand the company's brands are paired with: from a
 * list of its own (directory) or as its spelling (text). A channel showing the
 * company's brand as written has nothing to pair, and a marketplace whose
 * product write carries no brand is sent none, so both stay out of every brand
 * field and screen (ADR 0022 §1). Asked by the modes that pair rather than by
 * "not own", which read a marketplace taking no brand as one to map.
 */
export function takesBrand(mode: ChannelDescriptor['productRequirements']['brandMode'] | undefined): boolean {
    return mode === 'directory' || mode === 'text';
}

/**
 * The connection page's tabs a link may open, by the Turkish word each is
 * addressed by in `?sekme=`. A product's channel card names the settings every
 * product carries there, and links to where they are changed (EPZ-0116).
 */
const CHANNEL_SECTIONS = {
    genel: 'overview',
    ayarlar: 'settings',
} as const;

export interface ChannelPageSearch {
    sekme?: keyof typeof CHANNEL_SECTIONS;
}

/** The route's reading of its address: anything it does not know is dropped. */
export function validateChannelSearch(search: Record<string, unknown>): ChannelPageSearch {
    return typeof search.sekme === 'string' && search.sekme in CHANNEL_SECTIONS ? { sekme: search.sekme as keyof typeof CHANNEL_SECTIONS } : {};
}

/** The tab a connection page opens on. */
export function channelTab(search: ChannelPageSearch): string {
    return search.sekme ? CHANNEL_SECTIONS[search.sekme] : 'overview';
}

export function useCreateChannelAccount() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.credentials.write', {
        mutationFn: (body: CreateChannelAccountBody) => endpoints.createChannelAccount(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel-accounts'] }),
    });
}

/** Non-secret credential values for the edit form to prefill. Fetched only when
 *  a form is actually editing, and never cached, so a rotated value is never
 *  shown stale. */
/**
 * The cargo companies a marketplace works with, in its own ids. Fetched from
 * the channel on demand and cached for the session: the list changes about as
 * often as the marketplace signs a new carrier.
 */
export function useChannelCarriers(id: string, enabled: boolean) {
    return useQuery({
        queryKey: ['channel-carriers', id],
        queryFn: () => endpoints.channelCarriers(id),
        enabled: enabled && Boolean(id),
        staleTime: 10 * 60 * 1000,
    });
}

/**
 * What the channel says one of its own settings may be set to.
 *
 * Separate from the carriers above because it is a different kind of answer:
 * the carriers are a reference list every seller on the marketplace shares,
 * these are things this seller created in the marketplace's own panel.
 */
export function useChannelSettingOptions(id: string, key: string, enabled: boolean) {
    return useQuery({
        queryKey: ['channel-setting-options', id, key],
        queryFn: () => endpoints.channelSettingOptions(id, key),
        enabled: enabled && Boolean(id) && Boolean(key),
        staleTime: 10 * 60 * 1000,
    });
}

export function useChannelCredentials(id: string, enabled: boolean) {
    return useQuery({
        queryKey: ['channel-credentials', id],
        queryFn: () => endpoints.channelAccountCredentials(id),
        enabled,
        staleTime: 0,
        gcTime: 0,
    });
}

/** A connection's history — verifications, sync runs, failures — newest first. */
export function useChannelEvents(id: string, enabled: boolean) {
    return useQuery({
        queryKey: ['channel-events', id],
        queryFn: () => endpoints.channelAccountEvents(id),
        enabled,
    });
}

export function useUpdateChannelAccount() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.write', {
        mutationFn: ({ id, body }: { id: string; body: UpdateChannelAccountBody }) => endpoints.updateChannelAccount(id, body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel-accounts'] }),
    });
}

/**
 * Replacing a connection's credentials, with the name that is saved beside them.
 *
 * The same endpoint as a settings change, but a body carrying credentials needs
 * channel.credentials.write rather than channel.write, so the form that sends
 * them names that permission and the settings form keeps its own.
 */
export function useReplaceChannelCredentials() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.credentials.write', {
        mutationFn: ({ id, body }: { id: string; body: UpdateChannelAccountBody }) => endpoints.updateChannelAccount(id, body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel-accounts'] }),
    });
}

export function useVerifyChannelAccount() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.write', {
        mutationFn: (id: string) => endpoints.verifyChannelAccount(id),
        // Invalidated on settle rather than on success: a verification that fails
        // still changes the connection's recorded status, and leaving the old one
        // on screen would tell the seller their credentials are fine.
        onSettled: () => queryClient.invalidateQueries({ queryKey: ['channel-accounts'] }),
    });
}

export function useDeleteChannelAccount() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.write', {
        mutationFn: (id: string) => endpoints.deleteChannelAccount(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel-accounts'] }),
    });
}

export function useChannelSyncStatus(accountId: string, enabled = true) {
    return useQuery({
        queryKey: ['channel-sync', accountId],
        queryFn: () => endpoints.channelSyncStatus(accountId),
        // Only the channels that fetch orders have a sync to report; the storefront
        // writes its orders directly, so its status is never asked for.
        enabled,
    });
}

/**
 * Which of these accounts' order updates are failing right now.
 *
 * The channel list needs the answer for every row at once — a connection whose
 * updates fail belongs under "Sorunlu", not "Aktif" — and reads the same cache
 * each row's own status does, so the page asks once per account.
 */
export function useChannelSyncFailures(accounts: ChannelAccount[]): Set<string> {
    const results = useQueries({
        queries: accounts.map((account) => ({
            queryKey: ['channel-sync', account.id],
            queryFn: () => endpoints.channelSyncStatus(account.id),
        })),
    });
    return new Set(accounts.filter((account, index) => Boolean(currentSyncError(account, results[index]?.data))).map((account) => account.id));
}

/**
 * The failure of a connection's order updates that still speaks for it.
 *
 * A check that passed after the last failing attempt is the newer word: the
 * seller has just fixed the details, and the updates have not run since. Read
 * without it, a connection re-entered and checked went on showing "Bağlantı
 * Sorunu" and the old reason until the next update ran (observed 2026-09-18).
 */
export function currentSyncError(account: ChannelAccount, sync: ChannelSyncStatus | undefined): string | undefined {
    const error = sync?.lastError;
    if (!error) return undefined;
    const verified = account.verifiedAt ? Date.parse(account.verifiedAt) : NaN;
    const attempted = sync?.lastAttemptAt ? Date.parse(sync.lastAttemptAt) : NaN;
    if (account.status === 'connected' && verified > attempted) return undefined;
    return error;
}

/**
 * The status a connection is shown with.
 *
 * The stored status says whether the last check passed; the updates say whether
 * the connection works now, so a failing update wins. The list, the detail
 * header and the overview all read it here and never disagree about the same
 * connection.
 */
export function connectionStatus(account: ChannelAccount, updatesFailing: boolean): string {
    return account.status === 'connected' && updatesFailing ? 'failed' : account.status;
}

/**
 * One connection's shown status, as its header badge reads it, and whether it
 * is known yet: a marketplace's failing updates arrive only with its sync
 * status, and until then a stored "connected" may be stale.
 */
export function useConnectionStatus(account: ChannelAccount, channel: ChannelDescriptor | undefined) {
    const marketplace = channel?.kind === 'marketplace';
    const sync = useChannelSyncStatus(account.id, marketplace);
    return {
        status: connectionStatus(account, Boolean(currentSyncError(account, sync.data))),
        isPending: marketplace && sync.isPending,
    };
}

/** The badge colour of each status connectionStatus returns. */
export const CHANNEL_STATUS_TONES: Record<string, BadgeTone> = {
    connected: 'success',
    pending: 'warning',
    failed: 'danger',
    disabled: 'neutral',
};

export interface ChannelHealth {
    account: ChannelAccount;
    channel: ChannelDescriptor | undefined;
    /** The status a marketplace is shown with; the seller's own store reads as active or passive instead. */
    status: string;
    /** Counted under "Aktif": a working marketplace, or the own store while its shop is open. */
    active: boolean;
    /** Counted under "Sorunlu": a marketplace not yet checked, or failing. */
    broken: boolean;
}

/**
 * Every connection, counted the one way the panel counts them.
 *
 * The channel list's filters and the overview's summary both read this, so the
 * overview's "aktif" is the number under the list's "Aktif". The own store is
 * checked against nothing, so its stored status says nothing: it is active
 * while the shop is open, and a shop the seller closed is not a problem. For
 * the rest, not yet checked and failing both need the seller; a connection
 * they turned off themselves does not.
 */
export function useChannelHealth() {
    const channels = useChannels();
    const accounts = useChannelAccounts();
    const canOpen = useCanOpen();
    const sites = useStorefrontSites(canOpen('/magaza'));

    const byKey = new Map((channels.data?.channels ?? []).map((channel) => [channel.key, channel] as const));
    const list = accounts.data?.accounts ?? [];
    // A connected marketplace whose updates keep failing is not working,
    // however its stored status reads.
    const failing = useChannelSyncFailures(list.filter((account) => byKey.get(account.channelKey)?.kind === 'marketplace'));
    const shopOpen = sites.data?.sites?.[0]?.published ?? false;

    const rows: ChannelHealth[] = list.map((account) => {
        const channel = byKey.get(account.channelKey);
        const status = connectionStatus(account, failing.has(account.id));
        const ownStore = channel?.kind === 'own_store';
        return {
            account,
            channel,
            status,
            active: ownStore ? shopOpen : status === 'connected',
            broken: !ownStore && (status === 'pending' || status === 'failed'),
        };
    });

    return {
        rows,
        isPending: channels.isPending || accounts.isPending,
        isError: channels.isError || accounts.isError,
        // Read the way one query is, so the list's failure offers Tekrar Dene
        // (docs/11 §4): both reads are asked again, whichever failed.
        error: channels.error ?? accounts.error,
        isFetching: channels.isFetching || accounts.isFetching,
        refetch: () => Promise.all([channels.refetch(), accounts.refetch()]),
    };
}

// The classes whose cure is time rather than the seller, so their sentence says
// who tries again.
const WAITS = new Set(['rate_limited', 'transient', 'channel_down', 'unknown']);

/**
 * The sentence a seller reads for a stored channel failure.
 *
 * The API stores a failure by its class wherever the channel's own words would
 * not help, and keeps those words only for a refusal they explain
 * (channel.SellerMessage, docs/15-copy-and-formats.md §6). A class is written
 * out here; any other text is that explanation and is shown as it is. A wait
 * ends by saying who tries again: the scheduled updates retry by themselves,
 * while a check or a publish the seller started is theirs to repeat.
 *
 * A failure our own code decided about a listing or a publish arrives as a code
 * beside the text, and the text is then only the specifics its sentence quotes,
 * one to a line (listing.FailureCode, ADR 0005 §6). The code is read first; a
 * row stored before the codes has none and reads as above.
 */
export function channelProblemText(t: Messages, text: string, retry: 'automatic' | 'manual', code?: string): string {
    if (code) return listingFailureText(t, code, text, retry);
    const sentence: string | undefined = t.channels.problem[text as keyof typeof t.channels.problem];
    if (!sentence) return text;
    return WAITS.has(text) ? `${sentence} ${t.channels.problemRetry[retry]}` : sentence;
}

type FailureWords = string | ((...specifics: string[]) => string);

function listingFailureText(t: Messages, code: string, specifics: string, retry: 'automatic' | 'manual'): string {
    const words: FailureWords | undefined = (t.listings.failure as Record<string, FailureWords | undefined>)[code];
    // A code newer than this bundle: the specifics alone would read as noise.
    if (words === undefined) return `${t.channels.problem.unknown} ${t.channels.problemRetry[retry]}`;
    return typeof words === 'string' ? words : words(...specifics.split('\n'));
}

/**
 * What a channel status is called where a seller chooses one to send, and the
 * sentence under it where its name cannot carry its meaning.
 *
 * Both the order's own channel actions and the bulk status change offer the
 * same list, and a status named in one place and not the other is how "Paketi
 * Boz" stayed "Paket Açıldı" on a screen nobody looked at (EPZ-0057).
 */
export function channelStatusText(t: Messages, status: string): string {
    return t.orders.channelActions.statuses[status as keyof typeof t.orders.channelActions.statuses] ?? t.orders.channelActions.unknownStatus;
}

export function channelStatusHint(t: Messages, status: string): string | undefined {
    return t.orders.channelActions.statusHints[status as keyof typeof t.orders.channelActions.statusHints];
}

export function useChannelFeedStatus(accountId: string, enabled = true) {
    return useQuery({
        queryKey: ['channel-feed-status', accountId],
        queryFn: () => endpoints.channelFeedStatus(accountId),
        enabled,
    });
}

export function useSyncChannelNow() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.write', {
        mutationFn: (id: string) => endpoints.syncChannelNow(id),
        // The response is the fresh status, so it seeds the cache directly rather
        // than forcing a second round trip.
        onSuccess: (status, id) => queryClient.setQueryData(['channel-sync', id], status),
    });
}

/**
 * How far this connection's catalogue import has reached.
 *
 * Polled while a run is in flight and left alone otherwise: importing a
 * marketplace catalogue takes tens of minutes, so a screen that never updates
 * is a screen the seller reloads by hand, and one that polls for ever is a
 * request every few seconds for a job that runs daily.
 */
export function useProductImportStatus(accountId: string, enabled = true) {
    return useQuery({
        queryKey: ['product-import', accountId],
        queryFn: () => endpoints.productImportStatus(accountId),
        enabled,
        refetchInterval: (query) => (query.state.data?.running ? 5000 : false),
    });
}

export function useImportProductsNow() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.write', {
        mutationFn: (id: string) => endpoints.importProductsNow(id),
        onSuccess: (status, id) => {
            // The response is the fresh status, so it seeds the cache directly.
            queryClient.setQueryData(['product-import', id], status);
            // An import writes products and listings, so both lists are stale
            // the moment one is queued.
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['categories'] });
        },
    });
}

/** The inbound webhook URL, its registration and recent deliveries. Fetched
 *  only for a channel whose adapter can receive webhooks. */
export function useChannelWebhook(id: string, enabled: boolean) {
    return useQuery({
        queryKey: ['channel-webhook', id],
        queryFn: () => endpoints.channelWebhook(id),
        enabled,
    });
}

export function useRegisterChannelWebhook() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.write', {
        mutationFn: (id: string) => endpoints.registerChannelWebhook(id),
        onSuccess: (_result, id) => queryClient.invalidateQueries({ queryKey: ['channel-webhook', id] }),
    });
}

export function useUnregisterChannelWebhook() {
    const queryClient = useQueryClient();
    return usePermittedMutation('channel.write', {
        mutationFn: (id: string) => endpoints.unregisterChannelWebhook(id),
        onSuccess: (_result, id) => queryClient.invalidateQueries({ queryKey: ['channel-webhook', id] }),
    });
}
